/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Switch, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import GlobalStyle from 'global-styles';
import { Helmet } from 'react-helmet';
import { get, compact, isEmpty } from 'lodash';
import moment from 'moment';
import { createStructuredSelector } from 'reselect';
import injectSaga from 'utils/injectSaga';
import injectReducer from 'utils/injectReducer';
import RouteBuilder from 'components/RouteBuilder';
import { UNSUPPORTED_BROWSER } from 'utils/constants';
import { getPrimoBrandName } from 'utils/translation';
import { loadAuthentication } from 'containers/Authentication/actions';
import { AUTH_DEFAULT_EXPIRATION_TIMEOUT } from 'containers/Authentication/constants';
import { getDomainByHost } from 'utils/domainHelper';
import { dataLayerPush } from 'utils/tracking';
import ScrollToTop from 'containers/App/ScrollToTop';
import {
  selectLandingData,
  selectStylesTheme,
} from 'containers/Landing/selectors';
import {
  loadProductCategories,
  resetProductsError,
} from 'containers/PrimoProducts/actions';
import {
  selectPayMyBillError,
  selectBillingInformationError,
} from 'containers/PrimoProfile/selectors';
import {
  resetPayMyBillError,
  resetBillingInformationError,
} from 'containers/PrimoProfile/actions';
import {
  selectCustomerSummary,
  selectServiceLocationError,
} from 'containers/PrimoAccount/selectors';
import { resetServiceLocationError } from 'containers/PrimoAccount/actions';
import { removeAccessToken } from 'utils/accessTokenManager';
import { PRIMO_ACCCOUNT_PAGE_KEY } from 'containers/PrimoAccount/constants';
import { USER_REGION_KEY } from 'containers/UserRegion/constants';
import {
  LANDING_PAGE_KEY,
  SPARKLETTS_SLUG,
} from 'containers/Landing/constants';
import LandingPageSaga from 'containers/Landing/saga';
import LandingPageReducer from 'containers/Landing/reducer';

import primoAccountSaga from 'containers/PrimoAccount/saga';
import primoAccountReducer from 'containers/PrimoAccount/reducer';
import userRegionSaga from 'containers/UserRegion/saga';

import primoProductsSaga from 'containers/PrimoProducts/saga';
import { PRODUCTS_KEY } from 'containers/PrimoProducts/constants';

import MessageDialog from 'components/Dialogs/MessageDialog/Loadable';
import UnsupportedBrowserDialog from 'components/Dialogs/UnsupportedBrowserDialog/Loadable';
import SkipDeliveryModal from 'containers/PrimoAccount/NextDelivery/YourOrder/SkipDelivery/LoadableModal';

import { getBrandLegacyId } from 'components/Brands';
import { selectBrand, selectBranch } from 'components/Brands/selectors';
import {
  selectIsAuthenticated,
  selectIsInitiated,
} from 'containers/Authentication/selectors';
import Loader from 'components/Loader/index';
import { isSsr } from 'utils/ssrHelper';
import { getServerData } from 'utils/getServerData';
import {
  selectProductCategories,
  selectProductsError,
} from 'containers/PrimoProducts/selectors';
import store from 'utils/store';
import { getDomainRoutes } from './routes';
import RouteProvider from './RouteProvider';
import 'theme/styles.scss';

function pageViewDataLayerPushSelfServe({
  path,
  title,
  brand,
  custtype,
  branch,
}) {
  dataLayerPush(
    'SelfServe',
    {
      event: 'pageview',
      path,
      title,
      brand,
      branch,
      custtype,
    },
    'dlS0',
  );
}

class App extends PureComponent {
  constructor(props) {
    super(props);
    const { stylesTheme } = props;
    const isServersideRendering = isSsr();
    this.state = {
      stylesTheme,
      pushed: '',
      isServersideRendering,
      routes: [],
    };
    moment.locale('en');
  }

  getChildContext() {
    return { stylesTheme: this.state.stylesTheme };
  }

  static getDerivedStateFromProps(props, state) {
    const path = get(props, 'history.location.pathname');
    if (!state.path || (props.history && state.path && state.path !== path)) {
      return {
        path,
      };
    }

    if (state.stylesTheme !== props.stylesTheme) {
      return {
        stylesTheme: props.stylesTheme,
      };
    }
    return null;
  }

  componentDidMount() {
    // lll Language componentDidMount
    window.getReduxState = this.getReduxState;
    window.PrimoPathname = get(window, 'location.pathname', '');

    // FIXME remove all reliance on window.PrimoBrand,
    // make those using it use useBrand hook or its selector equivalent
    window.PrimoBrand =
      getBrandLegacyId(this.props.selectedBrand) || SPARKLETTS_SLUG;

    const routes = getDomainRoutes();
    this.setState({ routes });

    if (!this.state.isServersideRendering) {
      this.props.loadAuthentication();

      const expirationTimeout =
        parseInt(
          getServerData('AUTH_EXPIRATION_TIMEOUT') ||
            AUTH_DEFAULT_EXPIRATION_TIMEOUT,
          10,
        ) * 1000;

      this.checkSessionInterval = setInterval(
        this.props.loadAuthentication,
        expirationTimeout,
      );

      if (isEmpty(this.props.productCategories)) {
        this.props.loadProductCategories();
      }

      window.addEventListener('beforeunload', this.handleWindowClosing);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedBrand !== this.props.selectedBrand) {
      window.PrimoBrand =
        getBrandLegacyId(this.props.selectedBrand) || SPARKLETTS_SLUG;
    }
  }

  componentWillUnmount = () => {
    if (this.checkSessionInterval) {
      clearInterval(this.checkSessionInterval);
    }
  };

  getReduxState = () => store.getState().toJS();

  handleCloseErrorMessageDialog = () => {
    this.props.resetProductsError();
    this.props.resetPayMyBillError();
    this.props.resetServiceLocationError();
    this.props.resetBillingInformationError();
  };

  handleWindowClosing = () => {
    // checkAuth on didMount will get actual token everytime
    // To avoid API request with expired or incorrect token we need to clear every time we close page
    removeAccessToken();
  };

  handlePageChange = ({ title }) => {
    const {
      customerSummary: { custType: custtype },
      selectedBranch,
    } = this.props;

    if (!title) return;

    const path = get(this.props, 'history.location.pathname', '');
    const pushed = path;

    if (pushed !== this.state.pushed) {
      const { name: brand } = getDomainByHost(5);

      if (path === '/' && title !== brand) return;

      if (selectedBranch) {
        pageViewDataLayerPushSelfServe({
          path,
          title,
          custtype,
          brand,
          branch: selectedBranch,
        });
        this.setState({ pushed });
      }
    }
  };

  render() {
    const {
      props: {
        location: { pathname },
        site: {
          components: { page },
          name: siteName,
        },
        productsError,
        payMyBillError,
        serviceLocationError,
        billingInformationError,
      },
      state: { stylesTheme, isServersideRendering },
    } = this;
    const routes = isSsr() ? getDomainRoutes() : this.state.routes;
    const currentRoute = routes.find((route) => route.path === pathname);
    const pageTitle = compact([
      currentRoute && currentRoute.name,
      siteName,
    ]).join(' > ');
    const brandName = getPrimoBrandName();

    const metaData = {
      title: page?.metaData?.[brandName]?.title || page?.title || pageTitle,
      description:
        page?.metaData?.[brandName]?.description || page?.description || null,
    };

    if (!isServersideRendering && !this.props.isInitiated) {
      return <Loader />;
    }

    const errorMessage =
      productsError ||
      serviceLocationError ||
      payMyBillError ||
      billingInformationError;
    const unsupportedBrowser = errorMessage === UNSUPPORTED_BROWSER;
    const showNoIndex = page && !!page.noIndex;

    return (
      <Fragment>
        <RouteProvider>
          <Switch>
            <Redirect from="/products" to="/account/products/list" />
            {routes.map((routeProps) => (
              <RouteBuilder {...routeProps} />
            ))}
          </Switch>
        </RouteProvider>
        <Helmet
          htmlAttributes={{ lang: 'en' }}
          onChangeClientState={(helmetState) =>
            this.handlePageChange(helmetState)
          }
        >
          {showNoIndex && (
            <meta name="robots" content="noindex, nofollow"></meta>
          )}
          {(isEmpty(page) || !page.slug.includes('product-page')) && (
            <title>{metaData.title}</title>
          )}
          {!isSsr() && (
            <link rel="canonical" href={window.location.href.split('?')[0]} />
          )}
          <meta name="description" content={metaData.description} />
        </Helmet>
        <ScrollToTop />
        <SkipDeliveryModal />
        {!isServersideRendering && !unsupportedBrowser && errorMessage && (
          <MessageDialog
            open
            onClose={() => this.handleCloseErrorMessageDialog()}
            message={errorMessage}
            isErrorMessage
          />
        )}
        {unsupportedBrowser && <UnsupportedBrowserDialog />}
        <GlobalStyle theme={stylesTheme} />
      </Fragment>
    );
  }
}

App.childContextTypes = {
  stylesTheme: PropTypes.string,
};

App.propTypes = {
  isAuthenticated: PropTypes.bool,
  loadAuthentication: PropTypes.func,
  loadProductCategories: PropTypes.func,
  isInitiated: PropTypes.bool,
  payMyBillError: PropTypes.string,
  serviceLocationError: PropTypes.string,
  productsError: PropTypes.string,
  billingInformationError: PropTypes.bool,
  productCategories: PropTypes.array,
  selectedBrand: PropTypes.string,
  selectedBranch: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  site: selectLandingData(),
  stylesTheme: selectStylesTheme(),
  customerSummary: selectCustomerSummary(),
  isAuthenticated: selectIsAuthenticated(),
  isInitiated: selectIsInitiated(),
  payMyBillError: selectPayMyBillError(),
  serviceLocationError: selectServiceLocationError(),
  productsError: selectProductsError(),
  billingInformationError: selectBillingInformationError(),
  productCategories: selectProductCategories(),
  selectedBrand: selectBrand(),
  selectedBranch: selectBranch(),
});

const mapDispatchToProps = {
  loadAuthentication,
  loadProductCategories,
  resetProductsError,
  resetPayMyBillError,
  resetServiceLocationError,
  resetBillingInformationError,
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withAccountSaga = injectSaga({
  key: PRIMO_ACCCOUNT_PAGE_KEY,
  saga: primoAccountSaga,
});
const withAccountReducer = injectReducer({
  key: PRIMO_ACCCOUNT_PAGE_KEY,
  reducer: primoAccountReducer,
});

const withUserRegionSaga = injectSaga({
  key: USER_REGION_KEY,
  saga: userRegionSaga,
});

const withLandingPageReducer = injectReducer({
  key: LANDING_PAGE_KEY,
  reducer: LandingPageReducer,
});

const withLandingPageSaga = injectSaga({
  key: LANDING_PAGE_KEY,
  saga: LandingPageSaga,
});

const withProductsSaga = injectSaga({
  key: PRODUCTS_KEY,
  saga: primoProductsSaga,
});

export default compose(
  withRouter,
  withConnect,
  withAccountSaga,
  withAccountReducer,
  withUserRegionSaga,
  withLandingPageReducer,
  withLandingPageSaga,
  withProductsSaga,
)(App);
